import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExcelImportModal = ({ onClose, onImport }) => {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [importStatus, setImportStatus] = useState('idle'); // idle, processing, success, error
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragActive(false);
    const droppedFile = e?.dataTransfer?.files?.[0];
    if (droppedFile && (droppedFile?.type?.includes('excel') || droppedFile?.type?.includes('spreadsheet') || droppedFile?.name?.endsWith('.xlsx') || droppedFile?.name?.endsWith('.xls'))) {
      setFile(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setDragActive(false);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImportStatus('processing');
    
    // Simulate Excel processing
    setTimeout(() => {
      const mockResults = {
        totalRows: 45,
        successfulImports: 42,
        errors: [
          { row: 15, error: 'Missing exercise name' },
          { row: 28, error: 'Invalid category specified' },
          { row: 33, error: 'Difficulty level format incorrect' }
        ],
        warnings: [
          { row: 8, warning: 'Image URL not accessible' },
          { row: 22, warning: 'Equipment field empty' }
        ]
      };
      
      setImportResults(mockResults);
      setImportStatus('success');
      
      if (onImport) {
        onImport(mockResults);
      }
    }, 3000);
  };

  const downloadTemplate = () => {
    // Simulate template download
    const templateData = `Exercise Name,Category,Type,Description,Equipment,Target Muscles,Beginner Reps,Intermediate Reps,Advanced Reps,Instructions,Precautions,Image URL
Seated Leg Extensions,strength,dynamic,Strengthens quadriceps muscles,Leg extension machine,Quadriceps,8-10,12-15,15-20,Sit with back against pad and extend legs,Avoid if knee injury,https://example.com/image1.jpg
Arm Circles,flexibility,dynamic,Improves shoulder mobility,None,Shoulders,10-15,15-20,20-25,Stand with arms extended and make circles,Start with small circles,https://example.com/image2.jpg`;
    
    const blob = new Blob([templateData], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'exercise_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Import Exercises</h2>
            <p className="text-muted-foreground">
              Upload an Excel file to bulk import exercises
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="breathing-focus"
          >
            <Icon name="X" size={24} />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {importStatus === 'idle' && (
            <>
              {/* Template Download */}
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-foreground">Download Template</h3>
                    <p className="text-sm text-muted-foreground">
                      Get the Excel template with proper formatting
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadTemplate}
                    className="breathing-focus"
                  >
                    <Icon name="Download" size={16} className="mr-2" />
                    Template
                  </Button>
                </div>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Upload Excel File
                </label>
                <div
                  className={`
                    border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200
                    ${dragActive 
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }
                  `}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  {file ? (
                    <div className="space-y-4">
                      <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto">
                        <Icon name="FileSpreadsheet" size={32} className="text-success" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{file?.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(file?.size / 1024 / 1024)?.toFixed(2)} MB
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef?.current?.click()}
                        className="breathing-focus"
                      >
                        <Icon name="Upload" size={16} className="mr-2" />
                        Change File
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Icon name="Upload" size={48} className="text-muted-foreground mx-auto" />
                      <div>
                        <p className="text-foreground font-medium">
                          Drop Excel file here or click to upload
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Supports .xlsx and .xls files up to 10MB
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef?.current?.click()}
                        className="breathing-focus"
                      >
                        Select File
                      </Button>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Import Guidelines */}
              <div className="bg-muted/30 rounded-lg p-4">
                <h3 className="font-medium text-foreground mb-2">Import Guidelines</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Use the provided template for best results</li>
                  <li>• Exercise names must be unique</li>
                  <li>• Category must be one of: strength, cardio, flexibility, balance, rehabilitation, mobility</li>
                  <li>• Type must be either 'dynamic' or 'static'</li>
                  <li>• Repetition ranges should be in format '8-12' or single numbers</li>
                  <li>• Image URLs should be publicly accessible</li>
                </ul>
              </div>
            </>
          )}

          {importStatus === 'processing' && (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-6"></div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Processing Import</h3>
              <p className="text-muted-foreground">
                Analyzing and importing exercises from your file...
              </p>
            </div>
          )}

          {importStatus === 'success' && importResults && (
            <div className="space-y-6">
              {/* Success Summary */}
              <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Icon name="CheckCircle" size={24} className="text-success" />
                  <div>
                    <h3 className="font-semibold text-success">Import Completed</h3>
                    <p className="text-sm text-success/80">
                      {importResults?.successfulImports} of {importResults?.totalRows} exercises imported successfully
                    </p>
                  </div>
                </div>
              </div>

              {/* Errors */}
              {importResults?.errors?.length > 0 && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                  <h4 className="font-medium text-error mb-2">Errors ({importResults?.errors?.length})</h4>
                  <div className="space-y-1">
                    {importResults?.errors?.map((error, index) => (
                      <p key={index} className="text-sm text-error/80">
                        Row {error?.row}: {error?.error}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {importResults?.warnings?.length > 0 && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                  <h4 className="font-medium text-warning mb-2">Warnings ({importResults?.warnings?.length})</h4>
                  <div className="space-y-1">
                    {importResults?.warnings?.map((warning, index) => (
                      <p key={index} className="text-sm text-warning/80">
                        Row {warning?.row}: {warning?.warning}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={importStatus === 'processing'}
              className="breathing-focus"
            >
              {importStatus === 'success' ? 'Close' : 'Cancel'}
            </Button>
            
            {importStatus === 'idle' && (
              <Button
                onClick={handleImport}
                disabled={!file}
                className="breathing-focus"
              >
                <Icon name="Upload" size={16} className="mr-2" />
                Import Exercises
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcelImportModal;